import { useState, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext.jsx';
import { generateNextDay } from '../services/anthropic.js';

export function useDayGeneration() {
  const {
    weeks,
    userProfile,
    dailyFeedback,
    addGeneratedDay,
    addWeek,
    intakeMessages,
  } = useApp();

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const inFlightRef = useRef(new Set());

  const generateDay = useCallback(
    async (dayNumber, weekNumber = 1) => {
      if (!dayNumber) return null;
      const key = `${weekNumber}:${dayNumber}`;
      if (inFlightRef.current.has(key)) return null;
      inFlightRef.current.add(key);
      setIsGenerating(true);
      setError(null);

      try {
        const week = weeks.find((w) => w.weekNumber === weekNumber);
        const previousDays = week?.days || [];
        const transcript = intakeMessages || [];

        // Build a no-repeat block from EVERY week + day the user has
        // already received, so the model never recycles a scripture,
        // theme, or pull quote across the entire history.
        const allDays = (weeks || []).flatMap((w) => w.days || []);
        const usedContent = {
          scriptures: allDays
            .map((d) => d.scripture_reference)
            .filter(Boolean),
          themes: allDays.map((d) => d.title).filter(Boolean),
          pullQuotes: allDays.map((d) => d.pull_quote).filter(Boolean),
        };
        console.log('[useDayGeneration] Used content:', usedContent);

        const newDay = await generateNextDay(
          userProfile,
          dayNumber,
          weekNumber,
          previousDays,
          dailyFeedback || [],
          transcript,
          usedContent
        );

        if (weekNumber === 1) {
          addGeneratedDay(newDay);
        } else {
          // For week 2+, merge into the appropriate week directly via addWeek.
          const existing = week || {
            weekNumber,
            week_title: `Week ${weekNumber} — ${newDay.title || ''}`,
            week_theme: newDay.subtitle || '',
            days: [],
            totalDays: 5,
          };
          const filteredDays = (existing.days || []).filter(
            (d) => (d.dayNumber || d.day_number) !== dayNumber
          );
          const mergedDays = [...filteredDays, newDay].sort(
            (a, b) =>
              (a.dayNumber || a.day_number) -
              (b.dayNumber || b.day_number)
          );
          addWeek(weekNumber, { ...existing, days: mergedDays });
        }
        return newDay;
      } catch (err) {
        console.error('[useDayGeneration] Error:', err);
        setError(err?.message || 'Generation failed');
        return null;
      } finally {
        inFlightRef.current.delete(key);
        setIsGenerating(inFlightRef.current.size > 0);
      }
    },
    [
      weeks,
      userProfile,
      dailyFeedback,
      intakeMessages,
      addGeneratedDay,
      addWeek,
    ]
  );

  return { generateDay, isGenerating, error };
}
